import React from 'react';
import Logo from './Logo';

interface DashboardProps {
  partner: any;
  earnings: any;
  availableOrdersCount: number;
  onToggleAvailability: () => void;
  onViewOrders: () => void;
  onViewHistory: () => void;
  onViewProfile: () => void;
  onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({
  partner,
  earnings,
  availableOrdersCount,
  onToggleAvailability,
  onViewOrders,
  onViewHistory,
  onViewProfile,
  onLogout,
}) => {
  return (
    <div style={styles.container}>
      {/* Approval Status Warning */}
      {!partner.isApproved && (
        <div style={{
          background: '#ffa500',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '20px',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(255, 165, 0, 0.3)'
        }}>
          <h3 style={{margin: '0 0 10px', fontSize: '20px'}}>⏳ Account Pending Approval</h3>
          <p style={{margin: 0, fontSize: '16px'}}>
            Your account is waiting for admin approval. You cannot accept orders until approved.
          </p>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <Logo size={45} showText={true} />
        <div style={{flex: 1, marginLeft: '15px'}}>
          <h1 style={styles.title}>👋 Hello, {partner.name}</h1>
          <p style={styles.subtitle}>{partner.vehicleType} - {partner.vehicleNumber}</p>
        </div>
        <button onClick={onLogout} style={styles.logoutBtn}>
          Logout
        </button>
      </div>

      {/* Availability Toggle */}
      <div style={{
        ...styles.card,
        background: partner.isAvailable ? '#60b246' : '#dc143c',
        color: 'white',
      }}>
        <h2 style={{ margin: 0 }}>
          {partner.isAvailable ? '🟢 You are Online' : '🔴 You are Offline'}
        </h2>
        <button
          onClick={onToggleAvailability}
          style={styles.toggleBtn}
        >
          {partner.isAvailable ? 'Go Offline' : 'Go Online'}
        </button>
      </div>

      {/* Earnings Stats */}
      <div style={styles.statsGrid}>
        <div style={{...styles.statCard, background: '#ffa500'}}>
          <h3 style={styles.statValue}>₹{earnings.today || 0}</h3>
          <p style={styles.statLabel}>Today's Earnings</p>
        </div>
        <div style={{...styles.statCard, background: '#60b246'}}>
          <h3 style={styles.statValue}>₹{earnings.total || 0}</h3>
          <p style={styles.statLabel}>Total Earnings</p>
        </div>
        <div style={{...styles.statCard, background: '#28a745'}}>
          <h3 style={styles.statValue}>₹{earnings.tips || 0}</h3>
          <p style={styles.statLabel}>Total Tips 💰</p>
        </div>
        <div style={{...styles.statCard, background: '#5d8ed5'}}>
          <h3 style={styles.statValue}>{partner.totalDeliveries || 0}</h3>
          <p style={styles.statLabel}>Total Deliveries</p>
        </div>
        <div style={{...styles.statCard, background: '#9370db'}}>
          <h3 style={styles.statValue}>⭐ {partner.rating || 0}</h3>
          <p style={styles.statLabel}>Rating</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={styles.actions}>
        {partner.activeOrder && (
          <button onClick={() => (window as any).viewCurrentOrder?.()} style={{...styles.actionBtn, background: '#28a745'}}>
            🚴 Current Order
          </button>
        )}
        <button onClick={onViewOrders} style={{
          ...styles.actionBtn, 
          background: '#fc8019',
          position: 'relative'
        }}>
          📦 View Available Orders
          {availableOrdersCount > 0 && (
            <span style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: '#dc143c',
              color: 'white',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(220, 20, 60, 0.5)',
              animation: 'pulse 2s infinite'
            }}>
              {availableOrdersCount}
            </span>
          )}
        </button>
        <button onClick={onViewHistory} style={{...styles.actionBtn, background: '#5d8ed5'}}>
          📊 Delivery History
        </button>
        <button onClick={onViewProfile} style={{...styles.actionBtn, background: '#9c27b0'}}>
          👤 Profile & Payments
        </button>
      </div>

      {/* Weekly Stats */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📈 This Week</h3>
        <div style={styles.weeklyStats}>
          <div>
            <p style={styles.weeklyLabel}>Deliveries</p>
            <p style={styles.weeklyValue}>{earnings.weeklyDeliveries || 0}</p>
          </div>
          <div>
            <p style={styles.weeklyLabel}>Avg per Delivery</p>
            <p style={styles.weeklyValue}>₹{earnings.averagePerDelivery || 0}</p>
          </div>
        </div>
      </div>

      {/* Daily Target Progress */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>🎯 Daily Target Progress</h3>
        <div style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>Today's Deliveries</span>
            <span style={{ fontWeight: 'bold', color: '#fc8019' }}>{partner.todayDeliveries || 0} / 10</span>
          </div>
          <div style={{ 
            height: '12px', 
            background: '#e0e0e0', 
            borderRadius: '6px', 
            overflow: 'hidden' 
          }}>
            <div style={{ 
              height: '100%', 
              width: `${Math.min((partner.todayDeliveries || 0) * 10, 100)}%`, 
              background: 'linear-gradient(90deg, #fc8019, #ff6b35)',
              borderRadius: '6px',
              transition: 'width 0.5s ease'
            }}></div>
          </div>
          <p style={{ fontSize: '12px', color: '#888', marginTop: '8px', textAlign: 'center' }}>
            Complete 10 deliveries to earn ₹50 bonus! 🎁
          </p>
        </div>
        
        {/* Performance Badges */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {partner.rating >= 4.5 && (
            <span style={styles.badge}>⭐ Top Rated</span>
          )}
          {(partner.totalDeliveries || 0) >= 100 && (
            <span style={{...styles.badge, background: '#9c27b0'}}>🏆 100+ Deliveries</span>
          )}
          {(partner.totalDeliveries || 0) >= 50 && (partner.totalDeliveries || 0) < 100 && (
            <span style={{...styles.badge, background: '#2196f3'}}>🚀 50+ Deliveries</span>
          )}
          {partner.isAvailable && (
            <span style={{...styles.badge, background: '#4caf50'}}>🟢 Active</span>
          )}
        </div>
      </div>

      {/* Quick Tips */}
      <div style={{...styles.card, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white'}}>
        <h3 style={{...styles.cardTitle, color: 'white'}}>💡 Quick Tips</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>Stay online during peak hours (12-2 PM, 7-9 PM) for more orders</li>
          <li>Maintain good rating for priority order assignments</li>
          <li>Complete orders quickly to earn customer tips</li>
        </ul>
      </div>
    </div>
  );
};

const styles: any = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  title: {
    fontSize: '28px',
    color: '#333',
    margin: 0,
  },
  subtitle: {
    color: '#666',
    margin: '5px 0 0 0',
  },
  logoutBtn: {
    padding: '10px 20px',
    background: '#dc143c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  card: {
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  toggleBtn: {
    marginTop: '15px',
    padding: '12px 30px',
    background: 'white',
    color: '#333',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
  },
  statCard: {
    padding: '20px',
    borderRadius: '15px',
    color: 'white',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '28px',
    margin: '0 0 5px 0',
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: '14px',
    margin: 0,
    opacity: 0.9,
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
  },
  actionBtn: {
    padding: '20px',
    color: 'white',
    border: 'none',
    borderRadius: '15px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  cardTitle: {
    fontSize: '20px',
    color: '#333',
    marginTop: 0,
    marginBottom: '15px',
  },
  weeklyStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  },
  weeklyLabel: {
    color: '#666',
    margin: '0 0 5px 0',
    fontSize: '14px',
  },
  weeklyValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#fc8019',
    margin: 0,
  },
  badge: {
    background: '#fc8019',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
};

export default Dashboard;
